import React, { useState } from 'react';
import { HiOutlineInformationCircle, HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi';

// Plain-language help shown under every SuperAdmin report header, so a
// reader knows what the report is for and what each number means.

/** "7 days", "3 months", "1 year" — labels for the report period buttons. */
export const periodLabel = (days) => {
  if (days === 365) return '1 year';
  if (days === 180) return '6 months';
  if (days === 90) return '3 months';
  if (days === 60) return '2 months';
  return `${days} days`;
};

const storageKey = (id) => `report_guide_open_${id}`;

const readOpen = (id) => {
  try {
    const v = localStorage.getItem(storageKey(id));
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
};

/**
 * `summary` — one sentence: what this report answers.
 * `points` — [[term, meaning], ...] explaining the numbers and columns.
 * `tip` — optional "how to use it" line.
 * Expanded on first visit; the reader's choice is remembered per report.
 */
export const ReportGuide = ({ id, summary, points = [], tip }) => {
  const [open, setOpen] = useState(() => readOpen(id));

  const toggle = () => {
    setOpen((v) => {
      try { localStorage.setItem(storageKey(id), v ? '0' : '1'); } catch { /* storage unavailable */ }
      return !v;
    });
  };

  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
      <div className="flex items-start gap-3">
        <HiOutlineInformationCircle className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
        <p className="flex-1 text-sm text-gray-700">{summary}</p>
        {points.length > 0 && (
          <button
            type="button"
            onClick={toggle}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap"
          >
            {open ? 'Hide guide' : 'How to read this'}
            {open ? <HiOutlineChevronUp className="w-3.5 h-3.5" /> : <HiOutlineChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      {open && points.length > 0 && (
        <div className="mt-3 pl-8">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            {points.map(([term, meaning]) => (
              <div key={term} className="flex gap-2">
                <dt className="font-semibold text-gray-800 whitespace-nowrap">{term}:</dt>
                <dd className="text-gray-600">{meaning}</dd>
              </div>
            ))}
          </dl>
          {tip && <p className="mt-2 text-xs text-gray-500">Tip: {tip}</p>}
        </div>
      )}
    </div>
  );
};

export default ReportGuide;
